import { useEffect, useState } from 'react'

export interface GoogleReview {
  name: string
  text: string
  rating: number
}

export interface GoogleReviewsSnapshot {
  rating: number
  totalReviews: number
  reviews: GoogleReview[]
}

interface UseGoogleReviewsResult {
  data: GoogleReviewsSnapshot
  isLive: boolean
}

export function useGoogleReviews(
  fallback: GoogleReviewsSnapshot,
): UseGoogleReviewsResult {
  const [data, setData] = useState(fallback)
  const [isLive, setIsLive] = useState(false)

  useEffect(() => {
    const controller = new AbortController()

    async function loadReviews() {
      try {
        const response = await fetch('/api/google-reviews', {
          headers: { Accept: 'application/json' },
          signal: controller.signal,
        })
        if (!response.ok) return

        const payload = (await response.json()) as GoogleReviewsSnapshot
        if (
          typeof payload.rating !== 'number' ||
          typeof payload.totalReviews !== 'number' ||
          !Array.isArray(payload.reviews) ||
          payload.reviews.length === 0
        ) {
          return
        }

        setData({
          rating: payload.rating,
          totalReviews: payload.totalReviews,
          reviews: payload.reviews.slice(0, 3),
        })
        setIsLive(true)
      } catch (error) {
        if (error instanceof DOMException && error.name === 'AbortError') return
      }
    }

    void loadReviews()
    return () => controller.abort()
  }, [])

  return { data, isLive }
}
